import React, { useState, useEffect } from 'react';
import { 
  getFeeAnalytics,
  getFeeForecast
} from '../../services/api';
import { 
  FiRefreshCw, 
  FiAlertCircle,
  FiCheck,
  FiFilter,
  FiCalendar,
  FiTrendingUp,
  FiPieChart,
  FiUsers,
  FiActivity
} from 'react-icons/fi';
import { TbReceiptTax } from 'react-icons/tb';
import AnalyticsCard from './components/AnalyticsCard';
import ChartComponent from './components/ChartComponent';

function FeeAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [period, setPeriod] = useState('weekly');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAnalytics();
  }, [period, dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        period,
        date_from: dateRange.start,
        date_to: dateRange.end
      };

      const [analyticsResponse, forecastResponse] = await Promise.all([
        getFeeAnalytics(params),
        getFeeForecast(period)
      ]);

      if (analyticsResponse.success) {
        setAnalytics(analyticsResponse.data);
      } else {
        throw new Error(analyticsResponse.message || 'Failed to load analytics');
      }

      if (forecastResponse.success) {
        setForecast(forecastResponse.data);
      }
    } catch (err) {
      setError(`Failed to load analytics: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  return (
    <div className="min-h-full bg-[#f5f5f5] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0f0e0e]">Fee Analytics</h1>
            <p className="text-gray-600 mt-1">Track fee performance and trends</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2.5 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-all duration-200 font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiCheck className="text-green-500 text-xl" />
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-500 text-xl" />
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
              />
            </div>
          </div>
          
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center self-end"
          >
            <FiFilter className="mr-2" />
            Apply Filters
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <AnalyticsCard
          title="Total Fees"
          value={analytics?.summary?.total_fees || 0}
          change={10.5}
          icon={TbReceiptTax}
          color="orange"
          isLoading={loading}
        />
        
        <AnalyticsCard
          title="Platform Fees"
          value={analytics?.summary?.platform_fees || 0}
          change={8.2}
          icon={FiActivity}
          color="blue"
          isLoading={loading}
        />
        
        <AnalyticsCard
          title="Processor Fees"
          value={analytics?.summary?.processor_fees || 0}
          change={12.7}
          icon={FiPieChart}
          color="green"
          isLoading={loading}
        />
        
        <AnalyticsCard
          title="Transactions"
          value={analytics?.summary?.transaction_count || 0}
          change={15.3}
          icon={FiTrendingUp}
          color="purple"
          format="number"
          isLoading={loading}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Daily Trends */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-[#0f0e0e] mb-4">Fee Trends</h3>
          <div className="h-64">
            {analytics?.daily_trends ? (
              <ChartComponent
                data={analytics.daily_trends.map(item => ({
                  label: new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' }),
                  value: parseFloat(item.total_fees) || 0
                }))}
                type="line"
                color="#ff660e"
                height={200}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                No trend data available
              </div>
            )}
          </div>
        </div>

        {/* Fee Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-[#0f0e0e] mb-4">Fee Breakdown</h3>
          <div className="h-64">
            {analytics?.fee_breakdown ? (
              <ChartComponent
                data={analytics.fee_breakdown.map(item => ({
                  label: item.fee_type.replace('_', ' '),
                  value: parseFloat(item.total) || 0
                }))}
                type="bar"
                color="#10b981"
                height={200}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                No breakdown data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Users */}
      {analytics?.top_users && analytics.top_users.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-[#0f0e0e]">Top Fee Payers</h3>
            <FiUsers className="text-gray-400" />
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transactions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform Fees</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Processor Fees</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Fees</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {analytics.top_users.map((user, index) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#ff660e] to-orange-400 flex items-center justify-center text-white text-xs font-bold mr-3">
                          {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {user.transaction_count}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600 font-medium">
                      {formatCurrency(user.platform_fees_paid)}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600 font-medium">
                      {formatCurrency(user.processor_fees_paid)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-red-700">
                      {formatCurrency(user.total_fees_paid)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default FeeAnalytics;